/** Strips to at most 11 digits — canonical value for payloads and form state. */
export const cpfDigitsOnly = (value) =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 11);

/** Display-only mask ###.###.###-## (input may be digits or already partially masked). */
export const maskCpf = (value) => {
  const digits = cpfDigitsOnly(value);

  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
};

/** Strips to at most 14 digits — canonical value for CNPJ payloads and form state. */
export const cnpjDigitsOnly = (value) =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 14);

/** Display-only mask ##.###.###/####-## */
export const maskCnpj = (value) => {
  const digits = cnpjDigitsOnly(value);
  if (!digits.length) return "";

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(
      /^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/,
      "$1.$2.$3/$4-$5"
    );
};

/** DDD + número, até 11 dígitos (sem +55). Valor canônico para payload. */
export const phoneDigitsOnly = (value) =>
  String(value ?? "")
    .replace(/\D/g, "")
    .slice(0, 11);

/**
 * Telefone BR: (XX) XXXX-XXXX ou (XX) XXXXX-XXXX.
 * Aceita dígitos ou texto já parcialmente digitado.
 */
export const maskPhoneBr = (value) => {
  const d = phoneDigitsOnly(value);
  if (!d.length) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  }
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
};

/**
 * Celular BR com espaço após o 9: (XX) X XXXX-XXXX.
 * Aceita dígitos ou texto já parcialmente digitado.
 */
export const maskCellphoneBr = (value) => {
  const d = phoneDigitsOnly(value);
  if (!d.length) return "";
  if (d.length <= 2) return `(${d}`;
  if (d.length === 3) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 7) {
    return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3)}`;
  }
  return `(${d.slice(0, 2)}) ${d.slice(2, 3)} ${d.slice(3, 7)}-${d.slice(7, 11)}`;
};

export const maskCardNumber = value => {
  const digits = value.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
};

export const maskCardYear = value => {
  const digits = value.replace(/\D/g, '').slice(0, 4);

  if (digits.length < 4) return digits;

  const year = Number(digits);
  const currentYear = new Date().getFullYear();

  if (year < 1900) return '1900';
  if (year > 2099) return '2099';

  return String(year);
};

export function maskCep(value = '') {
  return value
    .replace(/\D/g, '')         
    .replace(/^(\d{5})(\d)/, '$1-$2')
    .slice(0, 9);               
}

export const maskPersonName = value =>
  value
    .replace(/[^a-zA-ZÀ-ÿ\s]/g, '')
    .replace(/\s+/g, ' ')
    .trimStart();

export function isValidCpf(value) {
  const cpf = cpfDigitsOnly(value);
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  const calcDigit = (base, factor) => {
    let total = 0;
    for (let i = 0; i < base.length; i += 1) {
      total += Number(base[i]) * (factor - i);
    }
    const remainder = (total * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  const firstDigit = calcDigit(cpf.slice(0, 9), 10);
  const secondDigit = calcDigit(cpf.slice(0, 10), 11);
  return firstDigit === Number(cpf[9]) && secondDigit === Number(cpf[10]);
}

/** Canonical: digits + optional comma + up to 2 decimal digits (no thousand separators). */
export function normalizeBrlCurrencyInput(input) {
  let s = String(input ?? "")
    .replace(/\./g, "")
    .replace(/[^\d,]/g, "");
  const ci = s.indexOf(",");
  if (ci === -1) {
    return s.replace(/^0+(?=\d)/, "") || s;
  }
  const intPart = s
    .slice(0, ci)
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "");
  const frac = s.slice(ci + 1).replace(/\D/g, "").slice(0, 2);
  const i = intPart === "" ? "0" : intPart;
  if (frac.length > 0) return `${i},${frac}`;
  return `${i},`;
}

/** Display: thousands with `.`, decimals with `,` (ex.: 1.234,56). */
export function maskBrlCurrencyDisplay(value) {
  if (value == null || value === "") return "";
  const str = String(value);
  const ci = str.indexOf(",");
  const intPart = (ci === -1 ? str : str.slice(0, ci)).replace(/\D/g, "");
  const frac =
    ci === -1 ? "" : str.slice(ci + 1).replace(/\D/g, "").slice(0, 2);
  if (!intPart && !frac) return "";
  const intNum = intPart || "0";
  const intFmt = intNum.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  if (ci === -1) return intFmt;
  if (str.endsWith(",") && frac.length === 0) return `${intFmt},`;
  /* Do not pad cents while typing — padEnd(2,"0") turned "45,9" into "45,90" and blocked "45,98". */
  return `${intFmt},${frac}`;
}

/** Decimal BR (juros %, etc.): digits + optional comma + up to 4 fraction digits. */
export function normalizeDecimalBrInput(input) {
  let s = String(input ?? "")
    .replace(/\./g, ",")
    .replace(/[^\d,]/g, "");
  const ci = s.indexOf(",");
  if (ci === -1) {
    return s.replace(/^0+(?=\d)/, "") || s;
  }
  const intPart = s
    .slice(0, ci)
    .replace(/\D/g, "")
    .replace(/^0+(?=\d)/, "");
  const frac = s.slice(ci + 1).replace(/\D/g, "").slice(0, 4);
  const i = intPart === "" ? "0" : intPart;
  if (frac.length > 0) return `${i},${frac}`;
  return `${i},`;
}

export function maskDecimalBrDisplay(value) {
  if (value == null || value === "") return "";
  const str = String(value);
  const ci = str.indexOf(",");
  const intPart = (ci === -1 ? str : str.slice(0, ci)).replace(/\D/g, "");
  const frac =
    ci === -1 ? "" : str.slice(ci + 1).replace(/\D/g, "").slice(0, 4);
  if (!intPart && !frac) return "";
  const intNum = intPart || "0";
  const intFmt = intNum.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  if (ci === -1) return intFmt;
  if (str.endsWith(",") && frac.length === 0) return `${intFmt},`;
  return `${intFmt},${frac}`;
}